import React, { useState, useEffect, useCallback } from "react";
import { Table, Tag, Tooltip, Divider, message } from "antd";
import type { TableProps } from "antd";
import request from "../../../utils/request";
import { SensitiveWordRecord, TabProps } from "../../../types/dataControl";

const SensitiveWordsTab: React.FC<TabProps> = ({ API_BASE_URL, token }) => {
  const [sensitiveWordLogs, setSensitiveWordLogs] = useState<
    SensitiveWordRecord[]
  >([]);
  const [loading, setSensitiveWordLoading] = useState<boolean>(false);
  const [total, setSensitiveWordTotal] = useState<number>(0);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
  });

  // 获取敏感词触发记录
  const fetchSensitiveWordLogs = useCallback(async () => {
    if (!token) return;

    setSensitiveWordLoading(true);
    try {
      const params: any = {
        page: pagination.current,
        page_size: pagination.pageSize,
      };

      const response = await request.get<{
        code: number;
        msg: string;
        data: { total: number; list: SensitiveWordRecord[] };
      }>(`${API_BASE_URL}/api/v3/admin/sensitive/logs`, {
        params,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.code === 200) {
        setSensitiveWordLogs(response.data.data.list);
        setSensitiveWordTotal(response.data.data.total);
      } else {
        message.error(response.data.msg || "获取敏感词触发记录失败");
      }
    } catch (error) {
      console.error("获取敏感词触发记录失败:", error);
      message.error("获取敏感词触发记录失败，请稍后重试");
    } finally {
      setSensitiveWordLoading(false);
    }
  }, [API_BASE_URL, token, pagination]);

  // 初始加载数据
  useEffect(() => {
    fetchSensitiveWordLogs();
  }, [fetchSensitiveWordLogs]);

  // 处理表格变化
  const handleTableChange: TableProps<SensitiveWordRecord>["onChange"] = (
    pagination
  ) => {
    setPagination({
      current: pagination.current || 1,
      pageSize: pagination.pageSize || 20,
    });
  };

  // 定义表格列
  const columns: TableProps<SensitiveWordRecord>["columns"] = [
    {
      title: "敏感词",
      dataIndex: "word",
      key: "word",
      width: 150,
      render: (word) => <Tag color="error">{word}</Tag>,
    },
    {
      title: "用户ID",
      dataIndex: "user_id",
      key: "user_id",
      width: 100,
      render: (userId) => <span>{userId === 0 ? "-" : userId}</span>,
    },
    {
      title: "IP地址",
      dataIndex: "ip",
      key: "ip",
      width: 150,
    },
    {
      title: "触发路径",
      dataIndex: "path",
      key: "path",
      width: 180,
    },
    {
      title: "触发内容",
      dataIndex: "text",
      key: "text",
      ellipsis: {
        showTitle: false,
      },
      render: (text) => (
        <Tooltip title={text}>
          <span>{text}</span>
        </Tooltip>
      ),
    },
    {
      title: "User Agent",
      dataIndex: "user_agent",
      key: "user_agent",
      width: 180,
      ellipsis: {
        showTitle: false,
      },
      render: (ua) => (
        <Tooltip title={ua}>
          <span>{ua}</span>
        </Tooltip>
      ),
    },
    {
      title: "触发时间",
      dataIndex: "time",
      key: "time",
      width: 170,
      render: (time) => new Date(time).toLocaleString("zh-CN"),
    },
  ];

  return (
    <>
      <Divider style={{ margin: "12px 0 24px" }} />

      {/* 敏感词列表表格 */}
      <Table
        columns={columns}
        dataSource={sensitiveWordLogs}
        rowKey={(record) => `${record.ip}-${record.time}-${record.word}`}
        loading={loading}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: total,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total) => `共 ${total} 条记录`,
        }}
        onChange={handleTableChange}
        scroll={{ x: 1300 }}
      />
    </>
  );
};

export default SensitiveWordsTab;
